/**
 * R228(레드팀 P2-8 ①, DESIGN 14.417) — **세계 막바지에 처음 온 사람의 도착 카드.**
 *
 * 남은 4시간에 친구 추천으로 들어온 신규가 「마지막 날이 오기 전에, 우리를 구해다오」를 읽었다 — 이미 마지막 날이었고, 봉헌은 닫혔고,
 * 종말 드래곤이 첫 마을을 떠나 들판을 비우고 있었다. 몇 시간 뒤 1레벨로 새 세계에 선다는 말도, 지금 들어와도 다음 세계에 무엇이
 * 남는지도 없었다. 카드 문장만 세계 단계에 따라 바꾼다(새 창·팝업 없음 — 절대규칙 4).
 *
 *   · 단계는 남은 시간으로 정한다 — 서버 종말 단계(`src/game/doom-stage.ts` `DOOM_STAGE_RULES`: 24시간 불길한 하늘 · 12시간 몬스터 폭주 ·
 *     6시간 마지막 날)와 같은 경계다. 마지막 날 길이는 `world/cycle.js` `FINAL_DAY_SECS`(check-client 미러 상수 검사가 서버와 대조).
 *   · 남은 시간 표기는 HUD 세계 시계와 같은 `worldLeftLabel`. 다음 세계 보너스 스탯은 서버 `pubSelf().carry.stats`(콘솔 [새 세계 특전]) —
 *     없으면(꺼짐·못 받음) 숫자 없이 뜻으로만 적는다.
 *   · 24시간보다 많이 남았으면 원래 문장 그대로다.
 */
import { S } from '../core/state.js';
import { STORY_LINES, STORY_OFFERING } from './story.js';
import { worldLeftSecs, worldLeftLabel, carryInfo, vaultFateText } from './world-end-notice.js';
import { FINAL_DAY_SECS } from '../world/cycle.js';
import { withJosa } from '../world/josa.js';

/** 도착 카드가 막바지 문장으로 바뀌는 남은 시간(초) — 서버 종말 단계 1(불길한 하늘, 24시간)과 같은 경계 */
export const ARRIVAL_LATE_SECS = 24 * 3600;

/** 다음 세계로 가져가는 것 한 마디 — 서버 값이 있으면 숫자로, 없으면 뜻으로. 특전이 꺼져 있으면 빈 문자열 */
function carryWords(y, left) {
  const c = carryInfo(y, left);
  if (c) return c.stats > 0 ? `지금 함께해도 다음 세계에 보너스 스탯 ${withJosa(`+${c.stats}`, '을')} 가져간다.` : '';
  return y?.carry && Number(y.carry.on) === 0 ? '' : '지금 함께해도 다음 세계로 가져가는 특전이 있다.';
}

/**
 * 도착 카드 줄. `left` = 세계가 끝나기까지 남은 초(모르면 null), `doom` = 서버 종말 행군 상태(`S.doom`).
 * 막바지가 아니면 원래 `STORY_LINES` 를 그대로 돌려준다(같은 배열).
 */
export function arrivalLines(y = S.you, left = worldLeftSecs(), doom = S.doom) {
  if (left === null || left <= 0 || left > ARRIVAL_LATE_SECS) return STORY_LINES;
  const final = left <= FINAL_DAY_SECS, remain = worldLeftLabel(left);
  const lines = STORY_LINES.slice(0, 3);   // 멸망을 앞둔 세계 · 주민 · 용사
  lines.push(final
    // 마지막 날에는 봉헌이 닫힌다(서버 world.ts — 마지막 날부터 봉헌 불가) · 행군 중이면 드래곤을 먼저 말한다
    ? (doom?.active ? '마지막 날이 시작됐다 — 종말 드래곤이 첫 마을을 떠나 세상을 가로지르고 있다. 드래곤 곁에는 가지 마라.'
      : '마지막 날이 시작됐다 — 생명의 돌 봉헌은 이미 닫혔다.')
    : STORY_LINES[3]);                     // 생명의 돌을 찾아 신전에 바쳐다오(마지막 날 전에는 아직 된다)
  lines.push(`이 세계는 ${remain} 뒤 끝난다 — 레벨과 가방은 1레벨에서 다시 시작한다. ${vaultFateText(y)}.`);
  const carry = carryWords(y, left);
  lines.push(`죽으면 가진 것을 떨군다(처음 몇 레벨은 지켜 준다).${carry ? ' ' + carry : ''}`);
  return lines;
}

/** 카드를 닫은 뒤 길잡이가 하는 말의 뒷부분 — 마지막 날에는 봉헌을 청하지 않는다(닫혔다) */
export function arrivalOffering(left = worldLeftSecs()) {
  return left !== null && left > 0 && left <= FINAL_DAY_SECS
    ? '마지막 날이 시작돼 봉헌은 닫혔어. 끝까지 살아남아서 다음 세계에서 다시 만나.'
    : STORY_OFFERING;
}
